/**
 * The frame every signed-in screen sits in: nav rail on desktop, tab bar on a
 * phone (salon-mockup.html navrail() / tabbar()). «Сообщения» carries the
 * unread count, as drawn: a pill on the rail, a badge on the tab bar.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useAuth } from '../auth';
import { api } from '../core/api';
import { syncNotificationBadge } from '../core/push';
import { ymd } from '../lib/time';
import { Icon, Mark } from './icons';

export type Tab = 'today' | 'calendar' | 'messages' | 'clients' | 'services' | 'settings';

const TABS: { id: Tab; icon: () => ReactNode; to: () => string; rail?: boolean }[] = [
  { id: 'today', icon: Icon.today, to: () => '/today' },
  { id: 'calendar', icon: Icon.cal, to: () => `/day/${ymd(new Date())}` },
  { id: 'messages', icon: Icon.msg, to: () => '/messages' },
  { id: 'clients', icon: Icon.ppl, to: () => '/clients' },
  { id: 'services', icon: Icon.scis, to: () => '/settings/services', rail: true },
  { id: 'settings', icon: Icon.set, to: () => '/settings' },
];

/** How often the badge and an open «Сообщения» check for new messages. */
export const INBOX_POLL_MS = 10_000;
export const inboxKey = (channel = '', q = '') => ['inbox', channel, q] as const;
export const unreadKey = ['unread'] as const;

/** The badge polls only the unread number (D-88); the full inbox loads on «Сообщения» alone. */
function useUnread(): number {
  const count = useQuery({ queryKey: unreadKey, queryFn: api.unreadCount, refetchInterval: INBOX_POLL_MS });
  const unread = count.data?.unread_total ?? 0;
  // The app icon shows the same number as the tab (Book&Host's badge sync).
  useEffect(() => {
    if (count.data) void syncNotificationBadge(unread);
  }, [count.data, unread]);
  return unread;
}

export function Shell({
  active,
  children,
  onTab,
}: {
  active: Tab;
  children: ReactNode;
  /** Tapping the active tab again (e.g. «Сегодня» scrolls back to now, D-9). */
  onTab?: (tab: Tab) => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const unread = useUnread();
  return (
    <div className="app">
      <div className="shell">
        <nav className="navrail">
          <div className="nr-brand">
            <b className="wm">
              <Mark className="mk" />
              Master<em>Day</em>
            </b>
          </div>
          {TABS.map(({ id, icon, to }) => (
            <Link key={id} to={to()} className={active === id ? 'on' : ''} onClick={() => onTab?.(id)}>
              {icon()}
              <span>{t(`nav.${id}`)}</span>
              {id === 'messages' && unread > 0 && (
                <em className="pill warn" style={{ marginLeft: 'auto' }}>
                  {unread}
                </em>
              )}
            </Link>
          ))}
          <div className="nr-sp" />
          <div className="nr-me">
            <span className="av" style={{ width: 28, height: 28, fontSize: 11 }}>
              {user?.name.charAt(0)}
            </span>
            <span className="grow ell">{user?.name}</span>
          </div>
        </nav>
        <div className="body">
          {children}
          <nav className="tabbar">
            {TABS.filter((tab) => !tab.rail).map(({ id, icon, to }) => (
              // A phone has no «Услуги» tab: services sit under «Настройки» (mockup tabbar('settings')).
              <Link key={id} to={to()} className={active === id || (id === 'settings' && active === 'services') ? 'on' : ''} onClick={() => onTab?.(id)}>
                {icon()}
                {id === 'messages' && unread > 0 && <em className="badge">{unread}</em>}
                <span>{t(`nav.${id}`)}</span>
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}

export function AppBar({
  title,
  sub,
  right,
  back,
  titleAfter,
}: {
  title: string;
  sub?: string;
  right?: ReactNode;
  back?: () => void;
  /** Shown right after the title and never cut; the title gives way with «…» (screen 10, header C2). */
  titleAfter?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <header className="appbar">
      {back && (
        <button type="button" className="iconbtn" onClick={back} aria-label={t('app.back')}>
          <Icon.back />
        </button>
      )}
      <div className="grow" style={{ minWidth: 0 }}>
        {titleAfter ? (
          <div className="ttl-row">
            <div className="ttl ell">{title}</div>
            {titleAfter}
          </div>
        ) : (
          <div className="ttl ell">{title}</div>
        )}
        {sub && <div className="sub">{sub}</div>}
      </div>
      {right}
    </header>
  );
}

/** Bottom sheet on a phone, centred modal on desktop (the mockup's .sheetwrap). */
export function Sheet({
  children,
  onClose,
  className = '',
}: {
  children: ReactNode;
  onClose: () => void;
  className?: string;
}) {
  return (
    <div className="sheetwrap open">
      <div className="scrim" onClick={onClose} />
      <div className={`sheet ${className}`} role="dialog" aria-modal="true">
        <div className="grab" />
        {children}
      </div>
    </div>
  );
}
